﻿/*
 * Purpose: Render presentational controls for OTP input and resend actions.
 * Boundary: Pure UI; no internal state or effects; fully controlled by parent.
 * Invariants:
 * - Input split into otpLength single-char fields with dashes.
 * - Button text reflects cooldown and request state.
 * - ASCII only; no Unicode in comments or strings.
 */

// Imports
import { useRef } from 'react';

// Component OtpInput:
// Controlled otpLength-field input for numeric OTP entry
export function OtpInput({ value, onChange, otpLength = 8, isValid = false }) {
    const inputs = useRef([]);
    const totalInputs = otpLength;
    const maxIndexInputs = otpLength - 1;

    // Helper to focus next/prev input
    const focusNext = (index) => {
        if (index < maxIndexInputs) inputs.current[index + 1].focus();
    };
    const focusPrev = (index) => {
        if (index > 0) inputs.current[index - 1].focus();
    };

    // Handler for input changes — also catches mobile paste/autofill that bypasses onPaste
    const handleChange = (index, e) => {
        if (isValid) return;
        const raw = e.target.value.replace(/[^0-9]/g, '');
        if (!raw) return;
        if (raw.length > 1) {
            const chars = raw.slice(0, totalInputs - index);
            const newValue = value.slice(0, index) + chars + value.slice(index + chars.length);
            onChange(newValue.slice(0, totalInputs));
            inputs.current[Math.min(index + chars.length, maxIndexInputs)]?.focus();
        } else {
            const newValue = value.slice(0, index) + raw + value.slice(index + 1);
            onChange(newValue);
            focusNext(index);
        }
    };

    // Extended handler for backspace/delete (merges original focus-only with new clearing)
    const handleKeyDown = (index, e) => {
        if (isValid) return; // Read-only when valid
        if (e.key === 'Backspace') {
            e.preventDefault();
            if (value[index]) {
                // Clear current char
                const newValue = value.slice(0, index) + '' + value.slice(index + 1);
                onChange(newValue);
                // Focus prev even if cleared (consistent behavior)
                focusPrev(index);
            } else {
                focusPrev(index);
            }
        } else if (e.key === 'Delete') {
            e.preventDefault();
            if (value[index]) {
                // Clear current char
                const newValue = value.slice(0, index) + '' + value.slice(index + 1);
                onChange(newValue);
            }
            // For empty, optionally focus next; here we stay put for simplicity
        }
    };

    // Handler for paste
    const handlePaste = (index, e) => {
        if (isValid) return; // Read-only when valid
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, totalInputs - index);
        if (pasted) {
            const newValue = value.slice(0, index) + pasted + value.slice(index + pasted.length);
            onChange(newValue);
            const nextIndex = Math.min(index + pasted.length, maxIndexInputs);
            inputs.current[nextIndex].focus();
        }
    };

    // Dynamic classes based on validation state
    const inputClasses = isValid
        ? "w-8 h-8 md:w-10 md:h-10 bg-emerald-900/20 rounded-md md:rounded-lg px-0 py-0 text-center text-xs md:text-base ring-1 ring-emerald-700/40 outline-none text-emerald-300 cursor-not-allowed"
        : "w-8 h-8 md:w-10 md:h-10 bg-neutral-900 rounded-md md:rounded-lg px-0 py-0 text-center text-xs md:text-base ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none";

    // Rendering - otpLength inputs with dashes
    return (
        <div className="inline-flex items-center gap-1 md:gap-2">
            {Array.from({ length: otpLength }).map((_, index) => (
                <>
                    {index > 0 && index % 3 === 0 && <span className="text-neutral-500 mx-0 md:mx-1">·</span>}
                    <input
                        key={index}
                        type="text"
                        value={value[index] || ''}
                        onChange={(e) => handleChange(index, e)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={(e) => handlePaste(index, e)}
                        ref={(el) => (inputs.current[index] = el)}
                        className={inputClasses}
                        maxLength={1}
                        inputMode="numeric"
                        readOnly={isValid}
                    />
                </>
            ))}
        </div>
    );
}

// Component ValidateButton:
// Green when OTP is fully entered and ready; dark/muted otherwise
export function ValidateButton({ onClick, disabled, isReady = false, isValid = false }) {
  const classes = isValid
    ? "w-full px-3 py-1 md:px-4 md:py-1 rounded-lg bg-emerald-900/40 text-emerald-400 text-sm md:text-base font-medium cursor-not-allowed"
    : isReady && !disabled
      ? "w-full px-3 py-1 md:px-4 md:py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 transition text-white text-sm md:text-base font-medium"
      : "w-full px-3 py-1 md:px-4 md:py-1 rounded-lg bg-neutral-900 text-white text-sm md:text-base font-medium ring-1 ring-neutral-800 disabled:opacity-60";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isValid}
      className={classes}
    >
      Validate
    </button>
  );
}

// Component RequestButton:
// Green = the next action the user should take. Yields green to Validate once OTP is fully entered.
export function RequestButton({ hasRequested, cooldown, onClick, disabled, isValid = false, otpReady = false }) {
    const text = hasRequested
        ? cooldown > 0
            ? `Resend (${cooldown})`
            : 'Resend'
        : 'Send Code';

    const classes = isValid
        ? "px-3 py-1 md:px-4 md:py-1 rounded-lg bg-neutral-900/40 text-neutral-500 text-sm md:text-base cursor-not-allowed"
        : otpReady
            ? "px-3 py-1 md:px-4 md:py-1 rounded-lg bg-neutral-900 text-white text-sm md:text-base ring-1 ring-neutral-800 focus:ring-emerald-600 disabled:opacity-60"
            : !disabled
                ? "px-3 py-1 md:px-4 md:py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 transition text-white text-sm md:text-base font-medium disabled:opacity-60"
                : "px-3 py-1 md:px-4 md:py-1 rounded-lg bg-neutral-900 text-white text-sm md:text-base ring-1 ring-neutral-800 focus:ring-emerald-600 disabled:opacity-60";

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || isValid}
            className={classes}
        >
            {text}
        </button>
    );
}